import { FC, useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { useEdifactPayload } from "../../../../../../../stores/Transactions/transaction.queries";

interface EdifactDisplayProps {
  exportId: string;
}

export const EdifactDisplay: FC<EdifactDisplayProps> = ({ exportId }) => {
  const [showEdifact, setShowEdifact] = useState(false);
  const {
    data: edifactPayload,
    isLoading,
    error,
  } = useEdifactPayload(exportId, showEdifact);

  const handleDownload = () => {
    if (!edifactPayload) return;

    // Create a blob from the edifact content
    const blob = new Blob([edifactPayload], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    // Create a temporary link and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.download = `edifact_${exportId}.txt`;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Paper
      sx={{
        height: "100%",
        overflow: "hidden",
        borderRadius: 2,
        bgcolor: "#D9DFE2",
        border: "1px solid #e0e0e0",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1,
          bgcolor: "white",
          color: "black",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CodeIcon fontSize="small" sx={{ color: "#757575" }} />
          <Typography variant="body2" fontWeight="500">
            EDIFACT Anzeigen
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {!showEdifact && (
            <Button
              size="small"
              variant="contained"
              startIcon={<VisibilityIcon sx={{ fontSize: 12 }} />}
              onClick={() => setShowEdifact(true)}
              sx={{
                bgcolor: "#9C1A35",
                color: "white",
                fontWeight: 500,
                height: 20,
                minWidth: "auto",
                px: 1,
                fontSize: "0.6rem",
                "&:hover": {
                  bgcolor: "#7A1529",
                },
              }}
            >
              Anzeigen
            </Button>
          )}
          {showEdifact && edifactPayload && !isLoading && !error && (
            <Button
              size="small"
              variant="contained"
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={handleDownload}
              sx={{
                bgcolor: "#9C1A35",
                color: "white",
                fontWeight: 500,
                height: 24,
                minWidth: "auto",
                px: 1,
                fontSize: "0.75rem",
                "&:hover": {
                  bgcolor: "#7A1529",
                },
              }}
            >
              herunterladen
            </Button>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 1,
          bgcolor: "#D9DFE2",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f5f5f5",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#bdbdbd",
            borderRadius: "6px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#9e9e9e",
          },
        }}
      >
        {!showEdifact ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <CodeIcon sx={{ fontSize: 50, opacity: 0.3, mb: 2 }} />
            <Typography variant="body2" align="center" color="text.secondary">
              Klicken Sie auf "Anzeigen", um den EDIFACT-Payload anzuzeigen
            </Typography>
          </Box>
        ) : isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">
            Fehler beim Laden der EDIFACT-Daten: {error.message}
          </Alert>
        ) : (
          <Box
            component="pre"
            sx={{
              fontFamily: "'Fira Code', 'Courier New', monospace",
              fontSize: "1rem",
              fontWeight: 500,
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              bgcolor: "white",
              color: "#000000",
              p: 2,
              borderRadius: 2,
              border: "1px solid #e0e0e0",
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            {edifactPayload || "Keine EDIFACT-Daten verfügbar"}
          </Box>
        )}
      </Box>
    </Paper>
  );
};
